/**
 * DraftRecoveryModal Component
 *
 * Modal dialog shown when a user returns to a survey with an existing draft.
 * Allows them to resume their progress or start over.
 */

import { memo, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import LinearProgress from '@mui/material/LinearProgress';
import { alpha } from '@mui/material/styles';

// Icons
import RestoreIcon from '@mui/icons-material/Restore';
import RefreshIcon from '@mui/icons-material/Refresh';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import { useSurveyTranslation, useCommonTranslation } from '@/i18n/hooks/useTypedTranslation';

export interface DraftRecoveryModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** Called when user chooses to resume */
  onResume: () => void;
  /** Called when user chooses to start over */
  onStartOver: () => void;
  /** Called when modal is closed without action */
  onClose: () => void;
  /** Last saved date of the draft */
  lastSavedAt: Date | null;
  /** Completion percentage (0-100) */
  completionPercentage: number;
}

/**
 * Formats a date for display (e.g., "Dec 28, 2025 at 2:30 PM")
 */
function formatDate(date: Date): string {
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }) + ' at ' + date.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
}

export const DraftRecoveryModal = memo(function DraftRecoveryModal({
  open,
  onResume,
  onStartOver,
  onClose,
  lastSavedAt,
  completionPercentage,
}: DraftRecoveryModalProps) {
  const { t } = useSurveyTranslation();
  const { t: tCommon } = useCommonTranslation();
  const [showConfirmStartOver, setShowConfirmStartOver] = useState(false);

  const handleStartOverClick = () => {
    setShowConfirmStartOver(true);
  };

  const handleConfirmStartOver = () => {
    setShowConfirmStartOver(false);
    onStartOver();
  };

  const handleCancelStartOver = () => {
    setShowConfirmStartOver(false);
  };

  const handleClose = () => {
    setShowConfirmStartOver(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
        },
      }}
    >
      {/* Header bar */}
      <Box sx={{ height: 4, bgcolor: 'primary.main' }} />

      <DialogTitle sx={{ pb: 1, pt: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              bgcolor: alpha('#14B8A6', 0.1),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <RestoreIcon sx={{ fontSize: 24, color: 'primary.main' }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {t('draft.recovery.title', 'Resume Your Survey?')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('draft.recovery.subtitle', 'You have unsaved progress')}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pb: 2 }}>
        {!showConfirmStartOver ? (
          <>
            {/* Progress info */}
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: alpha('#14B8A6', 0.04),
                border: 1,
                borderColor: alpha('#14B8A6', 0.1),
                mb: 2,
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {t('draft.recovery.progress', 'Progress')}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.main' }}>
                  {completionPercentage}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={completionPercentage}
                sx={{
                  height: 8,
                  borderRadius: 1,
                  bgcolor: alpha('#14B8A6', 0.1),
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 1,
                  },
                }}
              />
            </Box>

            {/* Last saved info */}
            {lastSavedAt && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary', mb: 2 }}>
                <AccessTimeIcon sx={{ fontSize: 18 }} />
                <Typography variant="body2">
                  {t('draft.recovery.lastSaved', { date: formatDate(lastSavedAt), defaultValue: `Last saved: ${formatDate(lastSavedAt)}` })}
                </Typography>
              </Box>
            )}

            <Typography variant="body2" color="text.secondary">
              {t('draft.recovery.message', 'Your previous answers have been saved. You can continue where you left off or start fresh.')}
            </Typography>
          </>
        ) : (
          <>
            {/* Confirm start over */}
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: alpha('#f59e0b', 0.08),
                border: 1,
                borderColor: alpha('#f59e0b', 0.2),
                mb: 2,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <WarningAmberIcon sx={{ color: 'warning.main', mt: 0.25 }} />
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: 'warning.dark', mb: 0.5 }}>
                    {t('draft.recovery.confirmTitle', 'Are you sure?')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('draft.recovery.startOverConfirm', 'Your saved progress will be permanently deleted. This action cannot be undone.')}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2, gap: 1 }}>
        {!showConfirmStartOver ? (
          <>
            <Button
              variant="outlined"
              color="inherit"
              onClick={handleStartOverClick}
              startIcon={<RefreshIcon />}
              sx={{ flex: 1 }}
            >
              {t('draft.recovery.startOver', 'Start Over')}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={onResume}
              startIcon={<RestoreIcon />}
              sx={{ flex: 1 }}
            >
              {t('draft.recovery.resume', 'Resume Survey')}
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="outlined"
              color="inherit"
              onClick={handleCancelStartOver}
              sx={{ flex: 1 }}
            >
              {tCommon('actions.cancel', 'Cancel')}
            </Button>
            <Button
              variant="contained"
              color="warning"
              onClick={handleConfirmStartOver}
              startIcon={<RefreshIcon />}
              sx={{ flex: 1 }}
            >
              {t('draft.recovery.confirmStartOver', 'Yes, Start Over')}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
});

export default DraftRecoveryModal;
